// ContactUsForm — the footer's "talk to a human" form.
//
// NOT a funnel step: no prospect, no meter, no funnel stores. The enquiry
// lands in Accounts' operator queue and surfaces in the CRM, where somebody
// reads it and replies by hand.
//
// ── FAILURE POSTURE (mirror of the BFF's) ─────────────────────────────────
// The ONLY job here is persistence, so unlike `ContactFirstPanel` (which lets
// the shopper continue when the lead write fails), a failed store shows an
// error and KEEPS the draft — a thank-you for a message that stored nowhere
// would be a lie to somebody explicitly asking to be contacted.
//
// The funnel session is minted on demand (`useEnsureSessionQuery`), exactly as
// the on-ramps do — the BFF's captcha/rate-limit gates are session-scoped.

import { useTranslation } from '@fluxora/i18n';
import { Button, Input, Label, Spinner, Textarea } from '@fluxora/ui';
import { useState } from 'react';

import { postContactUs } from '../api/contactUs.fetchers';
import { useEnsureSessionQuery } from '../api/serviceability.queries';
import {
  EMPTY_CONTACT_US,
  toContactUsRequest,
  validateContactUs,
  type ContactUsDraft,
  type ContactUsErrors,
} from '../services/contactUs.service';

export function ContactUsForm(): JSX.Element {
  const { t } = useTranslation();
  const session = useEnsureSessionQuery();

  const [draft, setDraft] = useState<ContactUsDraft>(EMPTY_CONTACT_US);
  const [errors, setErrors] = useState<ContactUsErrors>({});
  const [submitting, setSubmitting] = useState(false);
  const [failed, setFailed] = useState(false);
  const [sent, setSent] = useState(false);

  const set = (field: keyof ContactUsDraft, value: string): void => {
    setDraft((prev) => ({ ...prev, [field]: value }));
    setErrors((prev) => ({ ...prev, [field]: undefined }));
  };

  const handleSubmit = async (): Promise<void> => {
    const found = validateContactUs(draft);
    setErrors(found);
    if (Object.keys(found).length > 0) return;

    setSubmitting(true);
    setFailed(false);
    try {
      await postContactUs(toContactUsRequest(draft));
      setSent(true);
    } catch {
      // The draft stays on screen — nothing was stored, so nothing may be
      // cleared, and the visitor can simply try again.
      setFailed(true);
    } finally {
      setSubmitting(false);
    }
  };

  if (sent) {
    return (
      <p className="max-w-[36ch] text-xs leading-relaxed text-foreground/70" role="status">
        {t('shop.footer.contactUs.thanks')}
      </p>
    );
  }

  return (
    <div className="grid w-full grid-cols-1 gap-2 sm:grid-cols-2">
      <span className="text-xs font-semibold uppercase tracking-wide text-foreground/50 sm:col-span-2">
        {t('shop.footer.contactUs.title')}
      </span>

      {failed ? (
        <p className="text-[12px] text-[color:var(--color-danger,#dc2626)] sm:col-span-2" role="alert">
          {t('shop.footer.contactUs.failed')}
        </p>
      ) : null}

      <div className="flex flex-col gap-1">
        <Label htmlFor="contact-us-name" className="sr-only">
          {t('shop.footer.contactUs.name')}
        </Label>
        <Input
          id="contact-us-name"
          name="name"
          type="text"
          value={draft.name}
          placeholder={t('shop.footer.contactUs.name')}
          autoComplete="name"
          hasError={errors.name !== undefined}
          onChange={(e) => set('name', e.target.value)}
        />
        {errors.name !== undefined ? (
          <p className="text-[12px] text-[color:var(--color-danger,#dc2626)]">{errors.name}</p>
        ) : null}
      </div>

      <div className="flex flex-col gap-1">
        <Label htmlFor="contact-us-email" className="sr-only">
          {t('shop.footer.contactUs.email')}
        </Label>
        <Input
          id="contact-us-email"
          name="email"
          type="email"
          value={draft.email}
          placeholder={t('shop.footer.contactUs.email')}
          autoComplete="email"
          hasError={errors.email !== undefined}
          onChange={(e) => set('email', e.target.value)}
        />
        {errors.email !== undefined ? (
          <p className="text-[12px] text-[color:var(--color-danger,#dc2626)]">{errors.email}</p>
        ) : null}
      </div>

      <div className="flex flex-col gap-1 sm:col-span-2">
        <Label htmlFor="contact-us-message" className="sr-only">
          {t('shop.footer.contactUs.message')}
        </Label>
        <Textarea
          id="contact-us-message"
          name="message"
          rows={2}
          value={draft.message}
          placeholder={t('shop.footer.contactUs.message')}
          hasError={errors.message !== undefined}
          onChange={(e) => set('message', e.target.value)}
        />
        {errors.message !== undefined ? (
          <p className="text-[12px] text-[color:var(--color-danger,#dc2626)]">{errors.message}</p>
        ) : null}
      </div>

      <div className="flex justify-end sm:col-span-2">
        <Button
          variant="primary"
          shape="pill"
          className="cta-sheen"
          disabled={submitting || session.isPending}
          onClick={() => void handleSubmit()}
          data-testid="contact-us-submit"
        >
          {submitting ? <Spinner size="sm" /> : t('shop.footer.contactUs.cta')}
        </Button>
      </div>
    </div>
  );
}
